import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  BookOpen, 
  TrendingUp, 
  Settings, 
  User,
  Palette,
  ChevronRight,
  Flame,
  Menu,
  X
} from 'lucide-react';
import { Progress } from '../ui/progress';
import { apiFetch } from '../../lib/apiClient';

export const DashboardSidebar = () => {
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadProfile = async () => {
      const cached = sessionStorage.getItem('sidebar_profile');
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (isMounted) {
            setProfile(parsed);
            setLoading(false);
          }
          return;
        } catch (e) {
          // ignore parsing error
        }
      }

      try {
        const response = await apiFetch('/auth/profile', { method: 'GET' });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data?.message || data?.error || 'Unable to load profile.');
        }

        const newProfile = data.profile || data;
        sessionStorage.setItem('sidebar_profile', JSON.stringify(newProfile));

        if (isMounted) {
          setProfile(newProfile);
        }
      } catch {
        // Sidebar can fall back to defaults if profile fails.
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadProfile();

    const handleProfileUpdate = () => {
      sessionStorage.removeItem('sidebar_profile');
      if (isMounted) {
        setLoading(true);
        loadProfile();
      }
    };

    window.addEventListener('profileUpdate', handleProfileUpdate);

    return () => {
      isMounted = false;
      window.removeEventListener('profileUpdate', handleProfileUpdate);
    };
  }, []);

  const displayName = profile?.displayName || 'Artist';
  const email = profile?.email || '';
  const initials = (displayName || email)
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'AA';

  const styleTrack = profile?.styleTrack || profile?.currentRoadmap?.styleTrack || 'Concept Art Track';
  const streakCurrent = parseInt(profile?.streak?.current || '0', 10);
  const progressPercent = (() => {
    const roadmap = profile?.currentRoadmap;
    const completed = roadmap?.completedExercises?.length || 0;
    const total = roadmap?.availableExercises?.length || 0;
    if (!total) return 0;
    return Math.round((completed / total) * 100);
  })();

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', href: '/dashboard' },
    { icon: BookOpen, label: 'Roadmap', href: '/dashboard/roadmap' },
    { icon: TrendingUp, label: 'Progress', href: '/progress' },
  ];

  const bottomItems = [
    { icon: Settings, label: 'Settings', href: '/settings' },
    { icon: User, label: 'Profile', href: '/profile' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Mobile Top Navigation */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-[hsl(222,47%,11%)] z-50 flex items-center justify-between px-4 border-b border-white/5">
        <Link to="/" className="flex items-center gap-2 group">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[hsl(234,89%,63%)] to-[hsl(262,83%,58%)] flex items-center justify-center shadow-lg">
            <Palette className="w-4 h-4 text-white" />
          </div>
          <span className="text-lg font-bold text-white font-['Outfit']">
            Skill<span className="text-[hsl(262,83%,58%)]">Skulpt</span>
          </span>
        </Link>
        <button onClick={() => setIsOpen(!isOpen)} className="text-white p-2 focus:outline-none">
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar Desktop + Mobile */}
      <aside 
        className={`w-60 sidebar min-h-screen flex flex-col fixed left-0 top-0 z-50 transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`} 
        data-testid="dashboard-sidebar"
      >
        {/* Logo */}
      <div className="p-5 border-b border-white/5">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="sidebar-logo">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[hsl(234,89%,63%)] to-[hsl(262,83%,58%)] flex items-center justify-center shadow-lg">
            <Palette className="w-4.5 h-4.5 text-white" />
          </div>
          <span className="text-lg font-bold text-white font-['Outfit']">
            Skill<span className="text-[hsl(262,83%,58%)]">Skulpt</span>
          </span>
        </Link>
      </div>

      {/* User Progress Card */}
      <div className="mx-3 mt-5 p-4 rounded-xl bg-white/5 border border-white/5 min-h-[140px]" data-testid="user-progress-card">
        {loading ? (
          <div className="animate-pulse">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-full bg-white/10 shrink-0"></div>
              <div className="space-y-2.5 flex-1">
                <div className="h-3.5 bg-white/10 rounded w-24"></div>
                <div className="h-2.5 bg-white/10 rounded w-20"></div>
              </div>
            </div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-4 h-4 rounded-full bg-white/10"></div>
              <div className="h-3 bg-white/10 rounded w-16"></div>
            </div>
            <div className="space-y-2.5 mt-2">
              <div className="flex justify-between">
                <div className="h-2.5 bg-white/10 rounded w-20"></div>
                <div className="h-2.5 bg-white/10 rounded w-8"></div>
              </div>
              <div className="h-1.5 bg-white/10 rounded w-full"></div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[hsl(12,95%,65%)] to-[hsl(12,95%,55%)] flex items-center justify-center text-white font-semibold text-sm shrink-0">
                {initials}
              </div>
              <div>
                <p className="text-white text-sm font-medium">
                  {displayName}
                </p>
                <p className="text-slate-400 text-xs">{styleTrack}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 mb-3">
              <Flame className="w-4 h-4 text-[hsl(12,95%,65%)] streak-fire" />
              <span className="text-sm text-white font-medium">
                {streakCurrent} day streak
              </span>
            </div>
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Overall Progress</span>
                <span className="text-[hsl(262,83%,58%)] font-medium">
                  {progressPercent}%
                </span>
              </div>
              <Progress value={progressPercent} className="h-1.5 bg-white/10" />
            </div>
          </>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-5">
        <div className="space-y-0.5">
          {menuItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => setIsOpen(false)}
              data-testid={`sidebar-link-${item.label.toLowerCase().replace(' ', '-')}`}
              className={`sidebar-link flex items-center gap-3 px-3 py-2.5 text-sm font-medium group ${
                isActive(item.href)
                  ? 'active text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <item.icon className={`w-4.5 h-4.5 ${isActive(item.href) ? 'text-[hsl(234,89%,63%)]' : 'text-slate-500 group-hover:text-slate-300'}`} />
              {item.label}
              {isActive(item.href) && <ChevronRight className="w-3.5 h-3.5 ml-auto text-[hsl(234,89%,63%)]" />}
            </Link>
          ))}
        </div>
      </nav>

      {/* Bottom Navigation */}
      <div className="px-3 pb-5 border-t border-white/5 pt-4">
        <div className="space-y-0.5">
          {bottomItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => setIsOpen(false)}
              data-testid={`sidebar-link-${item.label.toLowerCase()}`}
              className={`sidebar-link flex items-center gap-3 px-3 py-2.5 text-sm font-medium ${
                isActive(item.href)
                  ? 'active text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <item.icon className="w-4.5 h-4.5" />
              {item.label}
            </Link>
          ))}
        </div>
      </div>
    </aside>
  </>
);
};

export default DashboardSidebar;
